import { useId, type ReactNode } from 'react';
import { Tooltip, TooltipPosition } from '../Tooltip';
import { clsx } from 'clsx';
import { useIntersect, UseIntersectReturn } from '../../../hooks';
import styles from './TooltipWrapper.module.scss';

type ChildProps = {
  childProps: {
    'aria-describedby'?: string;
  };
  tooltipId?: string;
};

type Props = {
  /** A function to render the wrapped children, returning a Node.
   *
   * It receives an object as an argument with this shape:
   * ```
   * {
   *   childProps: { // props for the rendered children
   *     ariaDescribedBy?: string, // aria attribute to put on the described element
   *   },
   *   // the unique id of the tooltip element
   *   tooltipId: string,
   * }
   * ```
   */
  children: (childProps: ChildProps) => ReactNode;
  /** The position to place the tooltip relative to the parent element. */
  position?: TooltipPosition;
  /** The content to render inside of the tooltip. */
  content: ReactNode;
  /** Indicates whether to render the component as a block-level element. */
  isBlock?: boolean;
  /** Indicates whether to flip the `Tooltip` based on available space. */
  shouldCorrectPosition?: boolean;
  /**
   * Indicates whether to show tooltips on touch-only devices,
   * i.e. devices that don't support hovering.
   */
  showTooltipOnTouchDevices?: boolean;
  /**
   * Indicates whether the `Tooltip` will be `aria-hidden`. Defaults
   * to `true` because `aria-describedby` will associate the "trigger"
   * to the content of the `Tooltip`, so the `Tooltip` should be hidden
   * from screenreaders to prevent it from being read twice. Set to `false`
   * to allow the `Tooltip` to be accessed directly by screenreaders. This
   * is most useful when the "trigger" node (the one with the `aria-describedby`
   * attribute on it) is _not_ interactive since most screenreaders don't read
   * the content associated via `aria-describedby` when the element with that
   * attribute on it is not interactive (and therefore the screenreader would
   * not read it twice).
   */
  tooltipAriaHidden?: boolean;
  /**
   * Optional prop to associate the tooltip and its control. If one
   * isn't supplied then one will be created. Passing this is helpful
   * when server and client ids are mismatched.
   */
  tooltipId?: string;
  /** Tooltip expands to accommodate content if true. */
  allowMultilineTooltip?: boolean;
  /** The `z-index` of the tooltip. */
  zIndex?: number;
};

const correctPosition = (
  position: TooltipPosition,
  result: UseIntersectReturn[1]
) => {
  const {
    isTopIntersecting,
    isBottomIntersecting,
    isLeftIntersecting,
    isRightIntersecting,
  } = result;
  if (isBottomIntersecting && position.includes('south')) {
    return position.replace('south', 'north') as TooltipPosition;
  } else if (isTopIntersecting && position.includes('north')) {
    return position.replace('north', 'south') as TooltipPosition;
  } else if (isLeftIntersecting && position.includes('west')) {
    return position.replace('west', 'east') as TooltipPosition;
  } else if (isRightIntersecting && position.includes('east')) {
    return position.replace('east', 'west') as TooltipPosition;
  }
  return position;
};

/**
 * ## ⚠️ This component is deprecated in favor of [Tooltip β](../?path=/docs/beta-tooltip--docs).
 *
 * Renders a `Tooltip` when the wrapped component is hovered or focused.
 *
 * @deprecated Use Tooltip β instead.
 */
export const TooltipWrapper = ({
  children,
  content,
  allowMultilineTooltip = undefined,
  isBlock = undefined,
  position = 'south',
  shouldCorrectPosition = true,
  showTooltipOnTouchDevices = true,
  tooltipAriaHidden = true,
  tooltipId: tooltipIdProp = undefined,
  zIndex = 10,
}: Props) => {
  const generatedTooltipId = useId();

  const tooltipId = tooltipIdProp || generatedTooltipId;

  const shouldRenderTooltip = !!content;

  const childProps = {
    'aria-describedby': shouldRenderTooltip ? tooltipId : undefined,
  };

  // Detect if the Tooltip would be rendered outside the vertical bounds
  // of the screen and if so invert the position.
  const threshold = 0.9;
  const rootMargin = `-25px`;
  const [setNode, result] = useIntersect({
    threshold,
    rootMargin,
  });
  const correctedPosition =
    shouldCorrectPosition && result && Object.keys(result).length > 0
      ? correctPosition(position, result)
      : position;

  return (
    <div
      className={styles.tooltipWrapper}
      data-touch-hidden={!showTooltipOnTouchDevices}
      data-block={isBlock}
      data-zds
      ref={setNode}
    >
      {children({
        childProps,
        tooltipId,
      })}
      {shouldRenderTooltip && (
        // Add `ariaHidden to `Tooltip` to prevent it from being accessible
        // and redundantly read by screenreaders.
        <div
          className={clsx(styles.tooltipWrapperContent, styles.positionStyles)}
          data-testid={`TooltipWrapper__content--${correctedPosition}`}
          data-zds
          data-position={correctedPosition}
          style={{
            zIndex,
          }}
        >
          <Tooltip
            allowMultiline={allowMultilineTooltip}
            aria-hidden={tooltipAriaHidden}
            id={tooltipId}
            position={null}
          >
            {content}
          </Tooltip>
        </div>
      )}
    </div>
  );
};
